import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Viewer,
    SceneMode,
    Cartesian3,
    Ion,
    defined,
    when,
    ScreenSpaceEventHandler,
    ScreenSpaceEventType,
    ImageryLayerFeatureInfo,
    Primitive,
    Cartesian2,
    Ellipsoid,
    Cartographic,
    Math as CesiumMath,
    Rectangle,
    BoundingSphere,
    HeadingPitchRange,
    RequestScheduler,
    Cesium3DTileFeature,
    Cesium3DTileset,
    EventHelper,
    Color,
    JulianDate,
} from 'cesium';
import { useUpdateEffect } from 'ahooks';
import { midpoint as TurfMidpoint, point as TurfPoint, distance as TurfDistance } from '@turf/turf';
import HighlightLayer from '../highlight-layer/HighlightLayer';
import { BaseMap, Imagery } from './base';
import './material';
import getDefaultMapInfo from '../_util/getDefaultMapInfo';
import "cesium/Build/Cesium/Widgets/widgets.css";
import classnames from 'classnames';
import { registerEventLayer } from '../events';
import Popup from './Popup';
import Provider from '../_util/Provider';
import Layers from './Layers';
import EventEmitter from '../_util/EventEmitter';
import useGetMapFeatureInfoByText from './hooks/useGetMapFeatureInfoByText';

import isObject from 'lodash-es/isObject';

import { createFeatureCollection } from '../feature-layer/createFeatureCollection';
import getBBoxFromGeojson from '../_util/getBBoxFromGeojson.js';
import { heightToZoom, zoomToHeight } from '../_util/heightAndZoom.js';
import { TdtLabelImageryProvider } from './base/imagery/tdt/TdtImageryProvider';
import { IRSLabelImageryProvider } from './base/imagery/irs/IRSImageryProvider';
import FeatureLayer from '../feature-layer/FeatureLayer';
import defaultTextEnum from './defaultTextEnum.js';
import { ConfigProvider, theme } from 'antd';
function Map(props) {
    const {
        width = '100%',
        height = '100%',
        dark = true,
        tk,
        children,
        onCloseTemplateBefore,
        onZoom,
        defaultPitch = -88.0,
        onEvents = {},
        baseMapUrl,
        duration = 3,
        baseMapStyle = {
            imageryProvider: 'TdtImageryProvider',
            style: 'dark',
        },
        autoView = true,
        closePopupAutoView = true,
        highlightColor = '#f7ff01',
        hideHighlight,
        wrapDomStyle,
        mapDomStyle,
        baseMapOptions,
        viewMode = '2D',
        baseColor,
        setLoading,
        customEnumText,
    } = props;

    const TextEnum = {
        ...defaultTextEnum,
        ...customEnumText,
    };

    const [mapLoaded, setMapLoaded] = useState(false);
    const [tipVis, setTipVis] = useState(false);
    const [tip, setTip] = useState(null);

    const mapRef = useRef(null);
    const mapDomRef = useRef(null);
    const tipRef = useRef(null);
    const popupRef = useRef();
    const getMapFeatureInfoByText = useGetMapFeatureInfoByText(mapRef);

    useEffect(() => {
        initMap();

        return () => {
            setLoading?.(false);
        };
    }, []);

    useUpdateEffect(() => {
        if (mapRef.current) {
            mapRef.current.baseMap.setBaseMap(baseMapStyle);
            addLabelImageryProvider();
        }
    }, [JSON.stringify(baseMapStyle)]);

    const addLabelImageryProvider = () => {
        if (!baseMapStyle.hideLabel) {
            // 调用影响中文注记服务
            if (baseMapStyle.imageryProvider === 'TdtImageryProvider') {
                let cia = new TdtLabelImageryProvider({ tk, ...baseMapStyle });
                mapRef.current.imageryLayers.addImageryProvider(cia); // 添加到cesium图层上
            } else if (baseMapStyle.imageryProvider === 'IRSImageryProvider') {
                let cia = new IRSLabelImageryProvider(baseMapStyle);
                mapRef.current.imageryLayers.addImageryProvider(cia); // 添加到cesium图层上
            }
        } else {
            if (mapRef.current) {
                let layers = mapRef.current.imageryLayers._layers || [];
                const ciaLayer = layers.find(item => item._imageryProvider?._layer === 'cia_w');
                if (ciaLayer) {
                    mapRef.current.imageryLayers.remove(ciaLayer);
                }
            }
        }
    };

    const initMap = () => {
        const {
            center,
            fitViewHeight,
            dark,
            zoom,
            createdMap,
            defaultView,
            encrypt,
            ...options
        } = props;

        let { minZoom, maxZoom, defaultZoomLevel, longitude, latitude } = getDefaultMapInfo();

        // 默认视野相关
        const viewDefault = {
            center: center || [
                defaultView?.x || longitude || 120.081627,
                defaultView?.y || latitude || 30.89638,
            ],
            zoom: zoom || (defaultZoomLevel && Number(defaultZoomLevel)) || 0,
            minZoom: (minZoom && Number(minZoom)) || 0,
            maxZoom: (maxZoom && Number(maxZoom)) || 20,
            pitch: viewMode === '3D' ? -35 : defaultPitch,
            ...defaultView,
        };

        if (viewDefault.zoom && !viewDefault.center[2]) {
            viewDefault.center[2] = zoomToHeight(viewDefault.zoom);
        }

        Ion.defaultAccessToken =
            'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJhZjc0ZDQ1ZS0wNWNhLTRhMDQtODJmYy03NTcyNDcxYzgzMDUiLCJpZCI6NzE4MTQsImlhdCI6MTYzNTQ3NzgyNX0.4TrdZWPWc9_6jLeGe90bRmBPB14XptwinSewVMu1iCE';

        let imageryProvider =
            baseMapStyle.imageryProvider &&
            Imagery[baseMapStyle.imageryProvider] &&
            new Imagery[baseMapStyle.imageryProvider]({
                style: baseMapStyle.style,
                url: baseMapUrl,
                crs: 'WGS84',
                dark,
                a: 11,
                ...baseMapStyle,
            });

        let map = new Viewer(mapDomRef.current, {
            shouldAnimate: true,
            animation: false, // 控制场景动画的播放速度控件
            baseLayerPicker: false, // 底图切换控件
            fullscreenButton: false, // 全屏控件
            geocoder: false, // 地理位置查询定位控件
            homeButton: false, // 默认相机位置控件
            timeline: false, // 时间滚动条控件
            infoBox: false, //是否显示信息框
            sceneModePicker: false, //是否显示3D/2D选择器
            selectionIndicator: false, // 点击点绿色弹出 是否显示选取指示器组件
            sceneMode: SceneMode.SCENE3D, //设定3维地图的默认场景模式: SceneMode.SCENE2D、SceneMode.SCENE3D、SceneMode.MORPHING
            navigationHelpButton: false, // 默认的相机控制提示控件
            scene3DOnly: true, // 每个几何实例仅以3D渲染以节省GPU内存
            navigationInstructionsInitiallyVisible: false,
            showRenderLoopErrors: process.env.NODE_ENV === 'development', //是否显示渲染错误
            orderIndependentTranslucency: false, //设置背景透明
            imageryProvider,

            ...options,
        });

        // Cesium 1.90 会通过 Viewer 自动添加 imageryProvider，新版本可能不会自动加入 imageryLayers。
        // 显式检查并补充初始底图，兼容不同 Cesium 版本且避免重复添加。
        if (imageryProvider) {
            const hasImageryProvider = map.imageryLayers._layers.some(
                layer => layer.imageryProvider === imageryProvider,
            );
            if (!hasImageryProvider) {
                map.imageryLayers.addImageryProvider(imageryProvider, 0);
            }
        }

        // 统一加密啊配置
        map.encrypt = encrypt;

        map.clock.currentTime = new JulianDate.fromIso8601('2024-01-26T12:00:00Z');

        map.viewDefault = viewDefault;

        // 设置地球暗色
        if (dark) {
            map.scene.globe.baseColor = Color.fromCssColorString('#152937');
        }

        // 设置地球颜色
        if (baseColor) {
            map.scene.globe.baseColor = Color.fromCssColorString(baseColor);
        }

        // 事件订阅
        map.eventEmitter = new EventEmitter();

        // 监听加载是否完成
        const helper = new EventHelper();
        helper.add(map.scene.globe.tileLoadProgressEvent, e => {
            setLoading?.(true);
            map.eventEmitter.emit('loading', e);
            if (e === 0) {
                setLoading?.(false);
            }
        });

        // 设置cesium请求调度器的最大并发数量
        RequestScheduler.maximumRequestsPerServer = 18;

        // 关闭请求调度器的请求节流
        RequestScheduler.throttleRequests = false;

        map.viewMode = viewMode;

        // 自动调整视角
        map.autoView = autoView;

        // 关闭气泡自动调整视角
        map.closePopupAutoView = closePopupAutoView;

        // 去版权信息
        map._cesiumWidget._creditContainer.style.display = 'none'; // 隐藏cesium ion

        mapRef.current = map;
        map.dark = dark;

        // 地图fit高度
        map.fitViewHeight = fitViewHeight || 5000;

        // 隐藏高亮效果
        map.hideHighlight = hideHighlight;

        // 底图服务
        map.baseMap = new BaseMap({
            map,
            imageryProvider: baseMapStyle.imageryProvider,
            style: baseMapStyle.style,
            layer: imageryProvider,
            options: baseMapOptions,
            TextEnum,
        });

        // 添加 dataSource 和 imagery 图层
        map.layers = new Layers({ map });

        // 高亮图层
        map.highlightLayer = new HighlightLayer({
            map,
            color: highlightColor,
        });

        // 图层改变的时候
        map.layers.on('change', layers => {
            onEvents.layersChange && onEvents.layersChange(layers);
            map.eventEmitter.emit('layersChange', layers);
        });

        // 图层新增
        map.layers.on('add', layer => {
            onEvents.layersAdd && onEvents.layersAdd(layer);
            map.eventEmitter.emit('layersAdd', layer);
        });

        // 图层移除
        map.layers.on('remove', layer => {
            onEvents.layersRemove && onEvents.layersRemove(layer);
            map.eventEmitter.emit('layersRemove', layer);
        });

        // 图层清空
        map.layers.on('clear', () => {
            onEvents.layersClear && onEvents.layersClear();
            map.eventEmitter.emit('layersClear');
        });

        // 图层展示隐藏
        map.layers.on('visible', (layer, visible) => {
            map.layers.onChange(); // 触发图层改变

            // 图层关闭时，隐藏自身的气泡
            if (!visible && map.popupRef) {
                map.hideTemplate(layer);
            }

            onEvents.layersVisible && onEvents.layersVisible(layer, visible);
            map.eventEmitter.emit('layersVisible');
        });

        // 图层变化
        map.layersChange = () => {
            map.layers.onChange();
        };

        // 获取 layers 里面的 key 集合
        map.getAllKeys = () => {
            return map.layers.getAllKeys();
        };

        // 获取当前展示的 keys
        map.getLayersShowKeys = () => {
            return map.layers.getShowKeys();
        };

        // 获取当前隐藏的 keys
        map.getLayersHiddenKeys = () => {
            return map.layers.getHiddenKeys();
        };

        // 根据 keys 设置图层的显示，其他的隐藏
        map.setLayersShowByKeys = _keys => {
            return map.layers.setShowByKeys(_keys);
        };

        // 根据 keys 设置图层的隐藏，其他的显示
        map.setLayersHideByKeys = _keys => {
            return map.layers.setHideByKeys(_keys);
        };

        // 添加图层
        map.addLayer = (layer, index) => {
            return map.layers.addLayer(layer, index);
        };

        // 移除图层
        map.removeLayer = layer => {
            return map.layers.removeLayer(layer);
        };

        // 清除图层和entity
        map.clearLayers = () => {
            return map.layers.clearLayers();
        };

        // 获取 ImageLayer 图层
        map.getImageLayers = keys => {
            return map.layers.getImageLayers(keys);
        };

        // 获取 FeatureLayer 图层
        map.getFeatureLayers = keys => {
            return map.layers.getFeatureLayers(keys);
        };

        // 获取 Imagery 图层最后一个 index 值
        map.getImageryLastIndex = _index => {
            let index = 1;
            const layers = map.getImageryLayers();
            if (layers?.length) {
                if (_index && layers.findIndex(v => _index < v.zIndex) !== -1) {
                    let _i = layers.findIndex(v => _index < v.zIndex) + 1;
                    index = _i < index ? _i : index;
                } else {
                    index = layers.length;
                }
            }

            return index;
        };

        // 查找所有dataSource和imagery图层
        map.getAllLayers = () => {
            const dataSources = map?.dataSources?._dataSources || [];
            const imageryLayers = map?.imageryLayers?._layers || [];
            const primitives = map?.scene?.primitives?._primitives || [];

            return [...dataSources, ...imageryLayers, ...primitives];
        };

        // 根据key查找imageryLayers图层
        map.getImageryLayers = _keys => {
            const layers = map?.imageryLayers?._layers || [];

            if (_keys && layers.length) {
                const keys = Array.isArray(_keys) ? _keys : [_keys];

                return layers.filter(layer => layer.key && keys.includes(layer.key));
            }

            return layers;
        };

        // 通过图形查询所属图层
        map.getLayerByFeature = entity => {
            if (!entity) return;
            return map.getAllLayers().find(layer => {
                if (entity instanceof ImageryLayerFeatureInfo) {
                    if (layer?.key && layer.key === entity?.imageryLayer?.key) {
                        return layer;
                    }
                } else if (
                    layer instanceof Cesium3DTileset &&
                    entity instanceof Cesium3DTileFeature
                ) {
                    if (layer?.key && layer.key === entity?.primitive?.key) {
                        return layer;
                    }
                } else if (layer instanceof Primitive) {
                    if (layer?.key && layer.key === entity?.primitive?.key) {
                        return layer;
                    }
                } else {
                    if (layer?.key && layer.key === entity?.key) {
                        return layer;
                    }
                }
            });
        };

        // 获取 3DTiles feature 属性
        map.get3DTileFeatureProperties = feature => {
            let properties = {};
            if (feature && feature.getPropertyNames) {
                const propertyIds = feature.getPropertyNames() || [];
                if (propertyIds?.length) {
                    const length = propertyIds.length;
                    for (let i = 0; i < length; ++i) {
                        const propertyId = propertyIds[i];
                        properties[propertyId] = feature.getProperty(propertyId);
                    }
                }
            }

            return properties;
        };

        // 获取entity中数据
        map.getProperties = feature => {
            if (feature instanceof Cesium3DTileFeature) {
                return map.get3DTileFeatureProperties(feature);
            } else if (feature?.primitive?.data && feature?.primitive instanceof Primitive) {
                return feature.primitive.data.find(item => item.id === feature.id);
            } else {
                let properties =
                    feature?._properties?.getValue(feature._id) || feature?.properties || {};
                properties = properties?.properties || properties;
                return properties;
            }
        };

        // 通过key查询图层
        map.getLayerByKey = key => {
            return map.getAllLayers().find(layer => {
                return key && layer.key && layer.key === key;
            });
        };

        // 根据id查询图层
        map.getLayerById = map.getLayerByKey;

        // 通过keys查询多个图层
        map.getLayerByKeys = keys => {
            return map.getAllLayers().filter(layer => {
                return layer.key && keys.includes(layer.key);
            });
        };
        map.destroyhighlightLayer = () => {
            map.highlightLayer.clearFeatures();
        };
        // 添加高亮entity
        map.addHighlightFeature = (feature, style) => {
            map.highlightLayer.addHighlightFeature(feature, style);
        };
        // 通过返回值控制是否执行关闭
        map.onCloseTemplateBefore = feature => {
            // 清除高亮
            map.destroyhighlightLayer();
            if (onCloseTemplateBefore) {
                return onCloseTemplateBefore(feature);
            }
        };

        // 根据key查找展示的图层
        map.getVisibleLayersByKeys = keys => {
            let layers = map.getAllLayers() || [];
            return layers.filter(layer => {
                if (typeof keys === 'string') {
                    return layer.key === keys && layer.show;
                } else if (Array.isArray(keys)) {
                    return keys.includes(layer.key) && layer.show;
                } else {
                    return layer.show && layer.key;
                }
            });
        };

        // 展示提示
        map.showTip = content => {
            setTip(content);
        };

        // 隐藏提示
        map.hideTip = () => {
            setTip(null);
        };

        // 根据图层条件设置图层选中
        map.showTemplateByFilter = async (key, field, value, _highlightLayer) => {
            let layer = map.getLayerByKey(key);
            if (layer?.show) {
                let obj = layer.getFeatureByFieldValue(field, value);
                if (obj) {
                    if (obj.then) {
                        obj.then(features => {
                            if (features?.length) {
                                let feature = features[0];
                                map.showTemplate(
                                    feature.feature,
                                    _highlightLayer || map.highlightLayer,
                                );
                            }
                        });
                    } else {
                        map.showTemplate(obj, _highlightLayer || map.highlightLayer);
                    }
                } else {
                    map.hideTemplate();
                }
            }
        };

        // 展示看板
        map.showTemplate = feature => {
            popupRef.current.showTemplate(feature);
        };

        // 隐藏看板
        map.hideTemplate = () => {
            popupRef.current.hideTemplate();
        };

        // 鼠标事件控制器
        let handleEvent = new ScreenSpaceEventHandler(map.scene.canvas);

        // 获取图层上的图形信息
        map.pickDataSourcesFeature = event => {
            if (event?.position) {
                const { position } = event;
                let pick = map.scene.pick(position);
                if (isObject(pick?.id)) {
                    pick = pick.id;
                }

                if (checkClickable(pick)) {
                    return;
                }

                return pick;
            }
        };

        // 根据关键字获取图形信息
        map.getMapFeatureInfoByText = getMapFeatureInfoByText;

        // 获取wms的图形数据
        map.pickImageryLayerFeatures = event => {
            if (event?.position) {
                const { position } = event;
                const pickRay = map.camera.getPickRay(position);

                return map.imageryLayers.pickImageryLayerFeatures(pickRay, map.scene);
            }
        };

        // 获取当前地图中心的经纬度
        map.getCenterPosition = () => {
            let centerResult = map.camera.pickEllipsoid(
                new Cartesian2(map.canvas.clientWidth / 2, map.canvas.clientHeight / 2),
            );
            let curPosition = Ellipsoid.WGS84.cartesianToCartographic(centerResult);
            let curLongitude = (curPosition.longitude * 180) / window.Math.PI;
            let curLatitude = (curPosition.latitude * 180) / window.Math.PI;
            return [curLongitude, curLatitude];
        };

        // 二三维切换
        map.setViewMode = type => {
            map.viewMode = type;
            map.fitView();
        };

        // 下标显隐
        map.setLabelVisible = visible => {
            map.getAllLayers().find(layer => {
                if (layer instanceof FeatureLayer) {
                    layer.label = visible;
                }
            });
        };

        // 获取地图视角信息
        map.getViewOptions = () => {
            //获取当前视角的pitch
            let pitch = map.camera.pitch;

            //获取当前视角的heading
            let heading = map.camera.heading;

            //获取当前视角的 position（位置）
            let position = map.camera.position;
            const cameraHeight = map.camera.positionCartographic.height;

            // 经纬高对象
            const cartographic = Cartographic.fromCartesian(position);
            const x = CesiumMath.toDegrees(cartographic.longitude);
            const y = CesiumMath.toDegrees(cartographic.latitude);

            return {
                z: cameraHeight,
                x: x,
                y: y,
                pitch: CesiumMath.toDegrees(pitch || 0.0),
                heading: CesiumMath.toDegrees(heading || 0.0),
            };
        };

        // 获取当前地图视图的边界
        map.getViewExtend = () => {
            let params = {};
            let extend = map.camera.computeViewRectangle();
            if (typeof extend === 'undefined') {
                //2D下会可能拾取不到坐标，extend返回undefined,因此作如下转换
                let canvas = map.scene.canvas;
                let upperLeft = new Cartesian2(0, 0); //canvas左上角坐标转2d坐标
                let lowerRight = new Cartesian2(canvas.clientWidth, canvas.clientHeight); //canvas右下角坐标转2d坐标

                let ellipsoid = map.scene.globe.ellipsoid;
                let upperLeft3 = map.camera.pickEllipsoid(upperLeft, ellipsoid); //2D转3D世界坐标

                let lowerRight3 = map.camera.pickEllipsoid(lowerRight, ellipsoid); //2D转3D世界坐标

                let upperLeftCartographic = map.scene.globe.ellipsoid.cartesianToCartographic(
                    upperLeft3,
                ); //3D世界坐标转弧度
                let lowerRightCartographic = map.scene.globe.ellipsoid.cartesianToCartographic(
                    lowerRight3,
                ); //3D世界坐标转弧度

                let minx = CesiumMath.toDegrees(upperLeftCartographic.longitude); //弧度转经纬度
                let maxx = CesiumMath.toDegrees(lowerRightCartographic.longitude); //弧度转经纬度

                let miny = CesiumMath.toDegrees(lowerRightCartographic.latitude); //弧度转经纬度
                let maxy = CesiumMath.toDegrees(upperLeftCartographic.latitude); //弧度转经纬度

                params.minx = minx;
                params.maxx = maxx;
                params.miny = miny;
                params.maxy = maxy;
            } else {
                //3D获取方式
                params.maxx = CesiumMath.toDegrees(extend.east);
                params.maxy = CesiumMath.toDegrees(extend.north);

                params.minx = CesiumMath.toDegrees(extend.west);
                params.miny = CesiumMath.toDegrees(extend.south);
            }

            // 返回屏幕所在经纬度范围
            return params;
        };

        // 根据camera高度近似计算当前层级
        map.heightToZoom = heightToZoom;

        // 根据当前层级近似计算camera高度
        map.zoomToHeight = zoomToHeight;

        // 注册事件
        registerEventLayer(map, props);

        // 注册地图组件事件
        function registerMapEvent(e, event) {
            if (map[event]) {
                map[event](e);
                onEvents[event] && onEvents[event](e);
            }
        }

        // 注册图层组件事件
        function registerEvent(feature, event, eventName) {
            if (feature) {
                let layer = map.getLayerByFeature(feature);
                if (layer?.[eventName] || feature[eventName]) {
                    let properties = map.getProperties(feature) || {};
                    if (feature instanceof ImageryLayerFeatureInfo && feature?.data) {
                        properties = {
                            ...properties,
                            data: feature?.data,
                        };
                    }

                    // 触发自身事件
                    feature[eventName]?.(properties, { feature, event });

                    if (layer?.[eventName]) {
                        layer[eventName](properties, { feature, event });
                    }
                }
            }
        }

        // 监测图层是否允许点击
        function checkClickable(feature) {
            let layer = map.getLayerByFeature(feature);
            let unclickable = false;
            // 无法单独设置group内的图层
            // 暂时为了取消行政区划影像图层的点击效果
            if (layer) {
                unclickable = layer.viewOnly || layer.unclickable;
            }
            return unclickable;
        }

        // 点击事件
        handleEvent.setInputAction(event => {
            event.lonLat = map.getPositionLonLat(event);
            map._clickObject = event;
            registerMapEvent(event, 'onClick');
            map.hideTip();

            // 是否禁止冒泡
            if (!map.cancelBubble) {
                const pick = map.scene.pick(event.position);
                if (pick instanceof Cesium3DTileFeature) {
                    let layer = map.getLayerByFeature(pick);
                    if (layer) {
                        if (layer.unclickable) {
                            return;
                        }

                        registerEvent(pick, event, 'onClick');

                        if (layer.template) {
                            map.onClickTemplateAfter && map.onClickTemplateAfter(pick);

                            map.showTemplate(pick);
                        }
                    }
                } else {
                    const entity = map.pickDataSourcesFeature(event);
                    const featuresPromise = map.pickImageryLayerFeatures(event);
                    if (entity) {
                        registerEvent(entity, event, 'onClick');

                        let layer = map.getLayerByFeature(entity?.[0] || entity);
                        if (Array.isArray(entity) || (layer && layer.template)) {
                            map.onClickTemplateAfter && map.onClickTemplateAfter(entity);
                            if (
                                Array.isArray(entity) &&
                                entity?.length > 1 &&
                                layer?.cluster?.hideTemplate
                            ) {
                                return;
                            }
                            map.showTemplate(entity);
                        }
                    } else if (defined(featuresPromise)) {
                        // 兼容1.98.0版本
                        const handleFeatures = features => {
                            features.sort((a, b) => {
                                return b.imageryLayer._layerIndex - a.imageryLayer._layerIndex;
                            });

                            const feature = features?.[0];
                            if (checkClickable(feature)) {
                                return;
                            }
                            registerEvent(feature, event, 'onClick');
                            map.showTemplate(feature);
                        };

                        // 针对低于 1.90.0 版本
                        if (when) {
                            when(featuresPromise, handleFeatures);
                        } else {
                            featuresPromise.then(handleFeatures);
                        }
                    }
                }
            }
        }, ScreenSpaceEventType.LEFT_CLICK);

        // 双击事件
        handleEvent.setInputAction(event => {
            map.trackedEntity = undefined; // 取消双击锁定

            event.lonLat = map.getPositionLonLat(event);

            registerMapEvent(event, 'onDoubleClick');

            const feature = map.pickDataSourcesFeature(event);

            registerEvent(feature, event, 'onDoubleClick');
        }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

        // 鼠标移动
        let hoverFeature;
        const getUid = feature => {
            if (feature && !feature._id) {
                feature._id = window.Math.random();
            }

            return feature._id;
        };

        // 鼠标移入移出
        handleEvent.setInputAction(event => {
            event.lonLat = map.getPositionLonLat({ position: event.endPosition });

            registerMapEvent(event, 'onMouseMove');
            const feature = map.pickDataSourcesFeature({ position: event.endPosition });

            if (feature) {
                let layer = map.getLayerByFeature(feature);
                mapDomRef.current.style.cursor = 'pointer'; // 设置手形光标
                if (layer && (layer.labelField || layer.tipTemplate)) {
                    const { labelField, tipTemplate } = layer;
                    const properties = map.getProperties(feature);

                    let label;
                    if (tipTemplate) {
                        label = tipTemplate(properties);
                    } else {
                        label = properties ? properties[labelField] : '';
                    }

                    if (label) {
                        map.showTip(label);
                    } else {
                        map.hideTip();
                    }
                } else {
                    map.hideTip();
                }

                registerEvent(feature, event, 'onMouseMove');

                // 判断是否在图形内移动
                if (hoverFeature) {
                    let uid = getUid(feature);
                    let hoverUid = getUid(hoverFeature);

                    // 判断是否进入新的图形
                    if (uid !== hoverUid) {
                        registerEvent(hoverFeature, event, 'onMouseLeave');

                        registerEvent(feature, event, 'onMouseEnter');
                        hoverFeature = feature;
                    }
                } else {
                    registerEvent(feature, event, 'onMouseEnter');
                    hoverFeature = feature;
                }

                registerEvent(feature, event, 'onMouseMove');
            } else {
                if (hoverFeature) {
                    registerEvent(hoverFeature, event, 'onMouseLeave');
                }
                mapDomRef.current.style.cursor = 'default'; // 恢复默认光标
                hoverFeature = null;
                map.hideTip();
            }
        }, ScreenSpaceEventType.MOUSE_MOVE);

        // 层级事件
        if (onZoom) {
            map.camera.moveEnd.addEventListener(() => {
                let viewRectangle = map.camera.computeViewRectangle();
                const west = (viewRectangle.west / window.Math.PI) * 180; // 左上方（西）
                const north = (viewRectangle.north / window.Math.PI) * 180; //右上方（北）
                const east = (viewRectangle.east / window.Math.PI) * 180; // 右下方（东）
                const south = (viewRectangle.south / window.Math.PI) * 180; // 左下方（南）
                const cameraHeight = map.camera.positionCartographic.height;
                const zoom = map.heightToZoom(window.Math.ceil(cameraHeight));
                onZoom && onZoom({ west, north, east, south, cameraHeight, viewRectangle, zoom });
            });
        }

        // 动态调整视角，不传参数为默认视角，参数可以是图层、图形、数据等
        map.fitView = e => {
            const {
                geoJson,
                geometryInfo,
                data,
                entity,
                lng,
                lat,
                height = 50000,
                options,
                ...rest
            } = e || {};
            let pitch = map.camera.pitch;

            // 判断是否点击了图形
            if (entity) {
                if (map.viewMode === '3D') {
                    map.flyTo(entity, { duration: 2 });
                } else {
                    map.flyTo(entity, {
                        duration: 2,
                        offset: {
                            heading: CesiumMath.toRadians(0.0),
                            pitch,
                            range: height,
                        },
                    });
                }
            } else if (lng && lat) {
                map.camera.flyTo({
                    destination: new Cartesian3.fromDegrees(Number(lng), Number(lat), height),
                    orientation: {
                        pitch: CesiumMath.toRadians(map.viewDefault.pitch || -88.0),
                        roll: 0.0,
                    },
                    duration: 2,
                    ...rest,
                });
            } else if (data || geometryInfo || geoJson) {
                let _geoJson = geoJson;

                if (geometryInfo) {
                    _geoJson = createFeatureCollection([{ geometryInfo }]);
                }

                if (data) {
                    _geoJson = createFeatureCollection(Array.isArray(data) ? data : [data]);
                }

                let box = getBBoxFromGeojson(_geoJson, {
                    padding: 100,
                    unit: 'px', // px | m
                    ...options,
                }); // 单位为米

                const point1 = TurfPoint([box[0], box[1]]);
                const point2 = TurfPoint([box[2], box[3]]);

                const centerPoint = TurfMidpoint(point1, point2);
                const z = TurfDistance(point1, point2) * (map.viewMode === '3D' ? 1100 : 1600);

                const heading = CesiumMath.toRadians(map.viewDefault.heading || 0.0);
                const pitch = CesiumMath.toRadians(map.viewDefault.pitch);

                const center = Cartesian3.fromDegrees(...centerPoint.geometry.coordinates);
                map.camera.flyToBoundingSphere(new BoundingSphere(center, z), {
                    offset: new HeadingPitchRange(heading, pitch, z),
                    duration: 2,
                });

                // map.camera.flyTo({
                //     destination: new Rectangle.fromDegrees(...box), //west, south, east, north,
                //     orientation: {
                //         pitch: map.viewMode === '3D' ? CesiumMath.toRadians(-35) : CesiumMath.toRadians(defaultPitch),
                //         // roll: 0.0,
                //     },
                //     duration: 2,
                //     ...rest,
                // });
            } else {
                let [x, y, z = 80000.0] = map.viewDefault.center;

                if (map.viewMode === '3D') {
                    const center = Cartesian3.fromDegrees(x, y);
                    const heading = CesiumMath.toRadians(map.viewDefault.heading || 0.0);
                    const pitch = CesiumMath.toRadians(-35.0);
                    map.camera.flyToBoundingSphere(new BoundingSphere(center, z), {
                        offset: new HeadingPitchRange(heading, pitch, z),
                        duration: 2,
                    });
                } else {
                    map.camera.flyTo({
                        destination: new Cartesian3.fromDegrees(x, y, z),
                        orientation: {
                            pitch: CesiumMath.toRadians(map.viewDefault.pitch || 0.0),
                            heading: CesiumMath.toRadians(map.viewDefault.heading || 0.0),
                            roll: 0.0,
                        },
                        duration: 2,
                    });
                }
            }
        };

        map.fitViewByOptions = options => {
            const { x, y, z, heading, pitch, duration } = options;
            if (x && y) {
                const _z = z || map.camera.positionCartographic.height;
                map.camera.flyTo({
                    destination: new Cartesian3.fromDegrees(x, y, _z), // 定位到范围中心点
                    orientation: {
                        heading: CesiumMath.toRadians(heading || 0.0), // 左右方向
                        pitch: CesiumMath.toRadians(pitch || 0.0), // 上下方向
                        roll: 0.0,
                    },
                    duration: duration || 1,
                });
            }
        };

        map.getPositionLonLat = event => {
            const cartesian = map.camera.pickEllipsoid(event.position, map.scene.globe.ellipsoid); // Cartesian3对象
            if (cartesian) {
                const cartographic = Cartographic.fromCartesian(cartesian); // 经纬高对象
                const lng = CesiumMath.toDegrees(cartographic.longitude);
                const lat = CesiumMath.toDegrees(cartographic.latitude);
                return [lng, lat];
            }

            return [];
        };

        if (duration) {
            // 自动调整视角
            map.fitView();

            // 延时触发回调事件
            setTimeout(() => {
                createdMap && createdMap(map, handleEvent);

                addLabelImageryProvider();

                onEvents.createdMap && onEvents.createdMap(map, handleEvent);

                setMapLoaded(true);
            }, (duration + 0.5) * 1000);
        } else {
            const [x, y, z = 80000.0] = map.viewDefault.center;
            if (map.viewMode === '3D') {
                const center = Cartesian3.fromDegrees(x, y);
                const heading = CesiumMath.toRadians(map.viewDefault.heading || 0.0);
                const pitch = CesiumMath.toRadians(map.viewDefault.pitch || -35.0);
                map.camera.flyToBoundingSphere(new BoundingSphere(center, z), {
                    offset: new HeadingPitchRange(heading, pitch, z),
                    duration: 0,
                });
            } else {
                map.camera.setView({
                    destination: new Cartesian3.fromDegrees(x, y, z),
                    orientation: {
                        pitch: CesiumMath.toRadians(map.viewDefault.pitch || 0.0),
                        heading: CesiumMath.toRadians(map.viewDefault.heading || 0.0),
                        roll: 0.0, // default value
                    },
                });
            }

            // 延时触发回调事件
            setTimeout(() => {
                createdMap && createdMap(map, handleEvent);

                addLabelImageryProvider();

                onEvents.createdMap && onEvents.createdMap(map, handleEvent);

                setMapLoaded(true);
            }, 500);
        }
    };

    const onMouseMove = event => {
        if (tip && tipRef.current) {
            tipRef.current.style.left = `${event.nativeEvent.layerX + 15}px`;
            tipRef.current.style.top = `${event.nativeEvent.layerY + 15}px`;
        }
    };

    return (
        <ConfigProvider
            theme={{
                algorithm: theme.darkAlgorithm,
                token: {
                    colorBgElevated: "rgba(0, 35, 65, 0.9)",
                    BorderRadiusLg: 6,
                    boxShadowSecondary: "0 0 5px rgba(1, 211, 252, 0.75) inset",
                    Select: {
                        colorBgContainer: 'rgba(22,50,75,.65)',
                        colorBorder: 'transparent',
                        colorTextQuaternary: '#fff'
                    },
                    Tree: {
                        colorBorder: "rgba(1, 211, 252, 0.5)",
                        colorBgContainer: 'rgba(22,50,75,.65)',
                    }



                }
            }}
        >
            <div
                style={{
                    width,
                    height,
                    background: baseColor || dark ? '#152937' : '#cccccc',
                    ...wrapDomStyle,
                }}
                className={classnames('cs-map-container')}
            >
                <div
                    ref={mapDomRef}
                    className={classnames('cs-map')}
                    onMouseMove={onMouseMove}
                    onMouseOver={() => setTipVis(true)}
                    onMouseOut={() => setTipVis(false)}
                    style={mapDomStyle}
                >
                    {tip && tipVis && (
                        <div ref={tipRef} className={classnames('custom-tip')}>
                            {tip}
                        </div>
                    )}
                </div>

                {mapLoaded && (
                    <Provider
                        value={{
                            map: mapRef.current,
                            showModal: props.showModal,
                            TextEnum: TextEnum,
                        }}
                    >
                        {children}
                        {mapRef.current && <Popup ref={popupRef} map={mapRef.current} />}
                    </Provider>
                )}
            </div>
        </ConfigProvider>
    );
}

export default Map;

Map.propTypes = {
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    createdMap: PropTypes.func,
    onCloseTemplateBefore: PropTypes.func,
    onZoom: PropTypes.func,
    center: PropTypes.array,
    zoom: PropTypes.number,
    minZoom: PropTypes.number,
    maxZoom: PropTypes.number,
    defaultPitch: PropTypes.number,
    dark: PropTypes.bool,
    baseColor: PropTypes.string,
    mapStyle: PropTypes.oneOfType(['vec', 'img']),
    tk: PropTypes.string.isRequired,
    onEvents: PropTypes.object,
    baseMapUrl: PropTypes.string,
    duration: PropTypes.number,
    baseMapStyle: PropTypes.object,
    children: PropTypes.node,
    autoView: PropTypes.bool,
    closePopupAutoView: PropTypes.bool,
    highlightColor: PropTypes.string,
    viewMode: PropTypes.string,
    showModal: PropTypes.func,
    wrapDomStyle: PropTypes.object,
    mapDomStyle: PropTypes.object,
    baseMapOptions: PropTypes.object,
    setLoading: PropTypes.func,
    fitViewHeight: PropTypes.number,
    hideHighlight: PropTypes.bool,
    defaultView: PropTypes.object,
    encrypt: PropTypes.bool,
    customEnumText: PropTypes.object,
};
